import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { AttendandeObject, IEmployee } from '../models/IEmployee';

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  data: any[];
  employeeCount = 0; // Variable to track the number of employees processed
  monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  constructor() { }

  private numberToAlphabet(num: number): string {
    let result = '';

    while (num > 0) {
      num--;
      result = String.fromCharCode((num % 26) + 65) + result;
      num = Math.floor(num / 26);
    }

    return result;
  }

  private getDatesInMonth(
    year: number,
    month: number,
    startDate: number = null,
    endDate: number = null
  ): string[] {
    const dates = [];
    dates.push('');
    dates.push('');
    const _date = new Date(year, month, startDate ? startDate : 1);
    while (_date.getMonth() === month) {
      dates.push(_date.getDate());
      _date.setDate(_date.getDate() + 1);
      if (endDate && _date.getDate() > endDate) {
        break;
      }
    }
    return dates as string[];
  }

  private padNumber(num: number): string {
    return num.toString().padStart(2, '0'); // Adds leading zero if needed
  }

  private addNewPage(
    workbook: ExcelJS.Workbook,
    start: Date,
    end: Date = null
  ) {
    try {
      const sheet = workbook.getWorksheet(
        this.monthNames[start.getMonth()] + '_' + start.getFullYear()
      );
      if (sheet) {
        return sheet;
      }
      const worksheet = workbook.addWorksheet(
        this.monthNames[start.getMonth()] + '_' + start.getFullYear()
      );
      // Generate dates for the specified month
      const dates = this.getDatesInMonth(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        end ? end.getDate() : null
      );

      // Add headers
      const titleRow1 = worksheet.addRow([
        `${this.monthNames[start.getMonth()]} (${start.getFullYear()})`,
      ]);
      titleRow1.getCell(1).alignment = { horizontal: 'center' };
      titleRow1.getCell(1).font = { bold: true, size: 12 };  // Adjust title font size
      titleRow1.getCell(1).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      // worksheet.getRow(1).height = 25;  // Adjust height of title row

      const titleRow2 = worksheet.addRow(['Name', '', 'Dates']);
      titleRow2.getCell(1).alignment = { horizontal: 'center' };
      titleRow2.getCell(1).font = { bold: true, size: 10 };  // Adjust title font size
      titleRow2.getCell(3).font = { bold: true, size: 10 };  // Adjust title font size
      titleRow2.getCell(1).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      // worksheet.getRow(1).height = 25;  // Adjust height of title row

      // Add dates horizontally
      const titleRow3 = worksheet.addRow(dates);
      titleRow3.eachCell((cell) => {
        cell.alignment = { horizontal: 'center' };
        // cell.font = { bold: true, size: 12, name: 'Arial' };
        cell.font = { bold: true, size: 10 };
      });
      titleRow3.getCell(1).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      // worksheet.getRow(3).height = 25;

      const totalCells = this.numberToAlphabet(dates.length);
      // console.log(totalCells);

      worksheet.mergeCells(`A1:${totalCells}1`); //Main Header
      worksheet.mergeCells(`C2:${totalCells}2`); //Date sub Header
      worksheet.mergeCells('A2:A3');
      worksheet.mergeCells('B2:B3');
      worksheet.getCell('A1').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell('A2').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell('C2').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };

      // Page Setup settings
      this.setWorksheetPageSetup(worksheet);

      return worksheet;
    } catch (error) {
      return null;
    }
  }

  private createDateSheet(start: Date, end: Date, workbook: ExcelJS.Workbook) {
    let worksheet: ExcelJS.Worksheet;

    // Data for each employee
    this.data.forEach((employee, index) => {
      let totalMonthHours = 0;
      let rowsData = [
        [employee.firstName + ' ' + employee.lastName, 'STATUS'],
        ['', 'IN'],
        ['', 'OUT'],
        ['', 'BREAK COUNT'],
        ['', 'BREAK TIME'],
        ['', 'TOTAL'],
      ];
      let cmonth = -1;
      // this.employeeCount = 0
      let totalPresent = 0;
      let totalAbsent = 0;

      // get weeks from it
      employee.groupedEmployeeAttendanceLogWeeks.forEach((week: any[]) => {

        // get each day from that
        week.forEach((day) => {
          let logdetails = null;
          let inTime = '-';
          let outTime = '-';
          let breakCount = '-';
          let BreakTime = '-';
          let totalHours = '-';
          let newS = new Date(day.date);
          if (start > newS || end < newS) {
            return;
          }

          // check if month is changed
          if (newS.getMonth() != cmonth) {

            // check if new month
            if (cmonth > -1) {
              // worksheet.addRows(rowsData);
              this.styleDataRows(worksheet, rowsData);
              worksheet.mergeCells(
                `A${(index + 1) * 6 - 2}:A${(index + 1) * 6 + 3}`
              );
              worksheet.getCell(`A${(index + 1) * 6 - 2}`).alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true
              };
              rowsData = [
                [employee.firstName + ' ' + employee.lastName, 'STATUS'],
                ['', 'IN'],
                ['', 'OUT'],
                ['', 'BREAK COUNT'],
                ['', 'BREAK TIME'],
                ['', 'TOTAL'],
              ];
            }
            cmonth = newS.getMonth();
            let temp = this.addNewPage(
              workbook,
              newS,
              end.getMonth() === cmonth ? end : null
            );
            if (temp) {
              worksheet = temp;
            }
          }
          if (day.status) {
            logdetails = day.data.employeeLogDetails;
            inTime = this.formatTime(new Date(logdetails[0].logInDate))
            // .toTimeString()
            // .split(' ')[0];
            outTime = this.formatTime(new Date(logdetails[logdetails.length - 1].logOutDate))
            // .toTimeString()
            // .split(' ')[0];
            breakCount = (logdetails.length - 1).toString();
            BreakTime = this.getTotalBreakTime(logdetails);
            totalHours = day.data.totalHours?.split(' ')[0];
          } else {
            inTime = '-';
            outTime = '-';
            breakCount = '-';
            BreakTime = '-';
            totalHours = '-';
          }

          rowsData[0].push(day.status ? 'P' : 'A');
          // rowsData[0].push(day.date)
          rowsData[1].push(inTime);
          rowsData[2].push(outTime);
          rowsData[3].push(breakCount);
          rowsData[4].push(BreakTime);
          rowsData[5].push(totalHours);
          totalPresent += day.status ? 1 : 0;
          totalAbsent += day.status ? 0 : 1;
          if (totalHours && totalHours != '-') {
            // Extract hours and minutes from the first element of the array
            let [hours, minutes] = totalHours.split(':').map(Number);

            // Convert hours and minutes to milliseconds
            let timeInMilliseconds = (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);

            totalMonthHours = totalMonthHours + timeInMilliseconds
          }
        });
      });
      // const rows = worksheet.addRows(rowsData);
      this.styleDataRows(worksheet, rowsData);  // Apply font size to data rows
      // const rows = worksheet.addRow([totalMonthHours]);
      let absentValue = totalAbsent - 4;
      let absentText = '';

      if (absentValue < 0) {
        absentText = 'Absent: 0    ';
      } else if (absentValue > 0) {
        absentText = 'Absent: ' + absentValue + '    ';
      }
      const rows = worksheet.addRow(['', absentText + '    Present: ' + totalPresent]);
      // worksheet.mergeCells(`A1:AG1`);
      worksheet.mergeCells(`A${(index + 1) * 7 - 3}:A${(index + 1) * 7 + 3}`);
      // worksheet.mergeCells(`A${(index + 1) * 6 - 2}:A${(index + 1) * 6 + 3}`);
      // worksheet.getCell(`A${(index + 1) * 6 - 2}`).style
      worksheet.getCell(`A${(index + 1) * 6 - 2}`).alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true
      };
    });
  }

  private styleDataRows(worksheet: ExcelJS.Worksheet, rowsData: any) {
    this.employeeCount++;
    // const lastRowNumber = worksheet.lastRow.number;
    rowsData.forEach((row: any[], index: number) => {
      const rows = worksheet.addRow(row);
      const lastRowNumber = worksheet.actualRowCount;
      rows.eachCell((cell, colNumber) => {
        if (colNumber !== 0) {
          cell.alignment = { horizontal: 'center' };
          cell.font = { size: 10 };
        }

        // Add borders around each cell
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };

      });
      if (index === 5 && (lastRowNumber > 3) && (lastRowNumber - 3) % 54 === 0) {
        rows.addPageBreak();
      }
    });
    // const rows = worksheet.addRow(rowsData[0]); 
  }

  private setWorksheetPageSetup(worksheet: ExcelJS.Worksheet): void {

    worksheet.headerFooter = {
      oddHeader: '&C&"Arial,Bold"&16 Employee Attendance Report',  // Centered header
      oddFooter: '&LPage &P of &N',  // Left footer
    };
    // worksheet.pageSetup.footer = 'Page &P of &N';
    // Page Setup - Adjusting to A4 size, landscape orientation, etc.
    worksheet.pageSetup = {
      // margins: {
      //   left: 0.3,
      //   right: 0.3,
      //   top: 0.3,
      //   bottom: 0.3,
      //   header: 0.3,
      //   footer: 0.3,
      // },
      paperSize: 9,
      orientation: 'landscape',
      // scale: 44,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    };

    worksheet.pageSetup.margins = {
      left: 0.3,
      right: 0.3,
      top: 0.7,
      bottom: 0.7,
      header: 0.3,
      footer: 0.3,
    };

    // Adjust column widths dynamically
    worksheet.columns = worksheet.columns.map((col, index) => {
      if (index === 0) {
        // For name column, set a larger width
        col.width = 15;
      } else if (index === 1) {
        // For date columns, use a fixed width
        col.width = 11.5;
      } else {
        col.width = 7.5;
      }
      return col;
    });

    // Optional: Display gridlines for print
    worksheet.pageSetup.showGridLines = true;

    // Ensure row heights are sufficient for headers
    worksheet.getRow(1).height = 30;  // Increase row height for header
    worksheet.getRow(2).height = 25;  // Adjust height for sub-headers or other rows

    // Repeat specific rows on every printed page
    worksheet.pageSetup.printTitlesRow = '1:3';

    // Repeat specific columns on every printed page
    // worksheet.pageSetup.printTitlesColumn = 'A:C';
  }


  // Function to calculate the difference between two Date objects
  private getTimeDifferenceInMinutes(date1: string, date2: string): number {
    const time1 = new Date(date1);
    const time2 = new Date(date2);

    // Ensure both dates are valid
    if (isNaN(time1.getTime()) || isNaN(time2.getTime())) {
      throw new Error('Invalid date format');
    }

    const diffMs: number = time2.getTime() - time1.getTime(); // Difference in milliseconds
    return Math.floor(diffMs / 1000 / 60); // Convert to minutes
  }

  private getTotalBreakTime(employeeLogDetails: any[]) {
    // Calculate total time difference across all log entries
    let totalMinutes = 0;

    for (let i = 0; i < employeeLogDetails.length - 1; i++) {
      const currentLog = employeeLogDetails[i];
      const nextLog = employeeLogDetails[i + 1];

      const timeDifference = this.getTimeDifferenceInMinutes(
        currentLog.logOutDate,
        nextLog.logInDate
      );
      totalMinutes += timeDifference;
    }

    // Convert total minutes to hours and minutes
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    return `${totalHours}:${remainingMinutes}`;
  }

  // Helper function to format date to "HH:mm"
  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0'); // Ensure 2-digit format
    const minutes = date.getMinutes().toString().padStart(2, '0'); // Ensure 2-digit format
    return `${hours}:${minutes}`;
  }

  exportToExcel(data: AttendandeObject): void {
    this.data = data.EmployeeDataSource;
    // const year = 2024; // Specify the year
    // const month = 0; // January (0 = January, 1 = February, ..., 11 = December)
    const start = data.startDate;
    const end = data.endDate;
    const workbook = new ExcelJS.Workbook();
    // new Date().setMonth(month);

    // const worksheet = workbook.addWorksheet(this.monthNames[month]);

    // this.exportMonthDates(start, end, workbook);
    this.createDateSheet(start, end, workbook);

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Attendance_${start.getFullYear()}_${this.monthNames[start.getMonth()]
        }-${this.monthNames[end.getMonth()]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  }
}
